import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { Component, ChangeDetectionStrategy, OnInit, Input, OnDestroy, ViewChild, ChangeDetectorRef, EventEmitter, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { MatAccordion, MatExpansionPanel } from '@angular/material/expansion';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatCheckboxChange } from '@angular/material/checkbox';

import moment from 'moment';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

import { IProstateCancerForm } from '@app/core/services/api/model/prostateCancer/prostateCancerCalcForm.model';
import { CalcValidators } from '@app/core/directives/validators/calc-validators';
import { CaseDataService } from '@app/core/services/case-data/case-data.service';
import { FormUtilsService } from '@app/core/services/form-utils.service';
import { ProstateCancerService } from '@app/core/services/api/api/prostatecancer.service';
import { SpinnerService } from '@app/core/services/spinner/spinner.service';

import {
  gleasonList,
  historicalGrade,
  KI67,
  metastasesOptions,
  nodalInvolvementOptions,
  prostateCancerTNM,
  psaAntigen,
  stages,
  tumorTypeList
} from './prostate-cancer-calc-inputs';

// Constants
const HAS_TREATMENT = 'hasTreatment';
const EXERESIS = 'exeresis';
const INTERVENTION_DATE = 'interventionDate';
const IN_COURSE = 'inCourse';
const START_DATE = 'startDate';
const END_DATE = 'endDate';
const OTHER_IN_COURSE = 'otherInCourse';
const CORTICOSTEROIDS_IN_COURSE = 'corticosteroidsInCourse';
const OTHER_START_DATE = 'otherStartDate';
const OTHER_END_DATE = 'otherEndDate';
const CORTICOSTEROIDS_START_DATE = 'corticosteroidsStartDate';
const CORTICOSTEROIDS_END_DATE = 'corticosteroidsEndDate';
const STAGE = 'stage';
const DIAGNOSIS_DATE = 'diagnosisDate';
const RIGHT_TO_FORGET = 'rightToForget';
const TUMOR_TYPE = 'tumorType';
const GLEASON = 'gleason';
const PSA = 'psa';
const KI67KEY = 'ki67';
const METASTASE_DETAIL = 'metastaseDetail';
const HISTOLOGICAL_GRADE = 'histologicalGrade';
const TUMOR_SIZE = 'tumorSize';
const LYMPH_NODE_DETAIL = 'lymphnodeDetail';

export interface IKeyValue {
  key: string | number;
  value: string;
}

@Component({
  selector: 'app-prostate-cancer-calc-form',
  templateUrl: './prostate-cancer-calc-form.component.html',
  styleUrls: ['./prostate-cancer-calc-form.component.scss'],
  providers: [FormGroupDirective],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProstateCancerCalcFormComponent implements OnInit, OnDestroy {
  @ViewChild('treatmentTypeAccordion') accordion: MatAccordion;
  @Input() data: IProstateCancerForm | null;
  @Output() dataResult: EventEmitter<any> = new EventEmitter();

  maxDate: Date;
  minDate = new Date(new Date().getFullYear() - 110, 0, 1);
  subscription: Subscription;

  // Basic data section
  showBirthDate = false;
  birthDate: string;
  tumorTypeList = [...tumorTypeList];
  gleasonList = [...gleasonList];
  basicDataForm: FormGroup;

  // Treatment section
  collapsableForm: FormGroup;
  surgicalForm: FormGroup;
  chemotherapyForm: FormGroup;
  radiotherapyForm: FormGroup;
  immunotherapyForm: FormGroup;
  medicineForm: FormGroup;
  treatmentForm: FormGroup;
  formError: boolean;

  // Histology section
  historicalGrade = [...historicalGrade];
  stages = [...stages];
  prostateCancerTNM: IKeyValue[] = [...prostateCancerTNM];
  nodalInvolvementOptions: IKeyValue[] = [...nodalInvolvementOptions];
  metastasesOptions: IKeyValue[] = [...metastasesOptions];
  psaAntigen = [...psaAntigen];
  ki67 = [...KI67];
  histologyForm: FormGroup;
  stageValid = false;
  histologyDisable = false;
  doCalcStage = true;

  errorMsgStage = new BehaviorSubject<string>('');
  isCalcStage = new BehaviorSubject<boolean>(false);
  stored: any;

  constructor(
    private fb: FormBuilder,
    private formUtils: FormUtilsService,
    private caseInProgressSrv: CaseDataService,
    private activatedRouter: ActivatedRoute,
    private loaderSrv: SpinnerService,
    private prostateCancerService: ProstateCancerService,
    private formGroupDirective: FormGroupDirective,
    private cdr: ChangeDetectorRef
  ) {
    this.maxDate = new Date();
    this.maxDate.setDate(this.maxDate.getDate() - 1);
  }

  ngOnInit() {
    this.subscription = new Subscription();

    // Initialize prostate cancer type form
    this.showBirthDate = !this.hasCaseInCourse() && !this.hasCaseInParams();

    if (this.hasCaseInCourse() && !this.data) {
      this.stored = JSON.parse(localStorage?.getItem('CASE_IN_COURSE'));
      this.initForm({ ...this.stored, birthDate: new Date(new Date().getFullYear() - this.stored?.age, 0, 1) });
    } else {
      this.initForm(this.data ? this.data : null);
    }
    this.subscription = new Subscription();

    // Initialize surgical form of treatment type block
    this.surgicalForm = this.fb.group({
      hasTreatment: [{ value: false, disabled: true }, []],
      exeresis: ['', []],
      interventionDate: ['', []]
    });

    // Initialize chemotherapy form of treatment type block
    this.chemotherapyForm = this.fb.group({
      hasTreatment: [{ value: false, disabled: true }, []],
      inCourse: ['', []],
      startDate: ['', []],
      endDate: [{ disabled: true, value: '' }, []]
    });

    // Initialize radiotherapy form of treatment type block
    this.radiotherapyForm = this.fb.group({
      hasTreatment: [{ value: false, disabled: true }, []],
      inCourse: ['', []],
      startDate: ['', []],
      endDate: [{ disabled: true, value: '' }, []]
    });

    // Initialize immunology form of treatment type block
    this.immunotherapyForm = this.fb.group({
      hasTreatment: [{ value: false, disabled: true }, []],
      inCourse: ['', []],
      startDate: ['', []],
      endDate: [{ disabled: true, value: '' }, []]
    });

    // Initialize medicine form of treatment type block
    this.medicineForm = this.fb.group({
      hasTreatment: [{ value: false, disabled: true }, []],
      corticosteroids: [false, []],
      corticosteroidsInCourse: ['', []],
      corticosteroidsStartDate: ['', []],
      corticosteroidsEndDate: [{ disabled: true, value: '' }, []],
      other: [false, []],
      otherInCourse: ['', []],
      otherStartDate: ['', []],
      otherEndDate: [{ disabled: true, value: '' }, []]
    });

    // Initialize treatement form
    this.treatmentForm = new FormGroup({});

    // Initialize histology form of histology block
    this.histologyForm = this.fb.group({
      histologicalGrade: ['', []],
      stage: [{ disabled: false }, [Validators.required, this.etapaValidator.bind(this)]],
      tumorSize: [{ disabled: false, value: '' }, []],
      lymphnodeDetail: [{ disabled: false, value: '' }, []],
      metastaseDetail: [{ disabled: false, value: '' }, []],
      psa: [{ disabled: false, value: '' }, []],
      ki67: [{ disabled: false, value: '' }, []]
    });

    // We init the form control listener
    this.initListenerForm();
    // If we have data, is edit mode
    if (this.data) {
      this.patchDataValues();
    }

    // Formulario para el div
    this.collapsableForm = this.fb.group({
      surgical: [this.surgicalForm.controls[HAS_TREATMENT].value],
      chemotherapy: [this.chemotherapyForm.controls[HAS_TREATMENT].value],
      radiotherapy: [this.radiotherapyForm.controls[HAS_TREATMENT].value]
    });

    this.formError = this.checkAllFieldsFalse(this.collapsableForm);
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  initForm = (caseData?: any) => {
    this.basicDataForm = this.fb.group({
      birthDate: [{ disabled: false, value: caseData ? caseData.birthDate : '' }, [CalcValidators.age(18, 100), Validators.required]],
      age: [{ disabled: true, value: caseData ? caseData.age : '' }, [Validators.required]],
      rightToForget: [{ disabled: false, value: caseData ? caseData.rightToForget : '' }, [Validators.required]],
      diagnosisDate: [{ disabled: false, value: caseData ? caseData.diagnosisDate : '' }, [Validators.required, this.fechaNoPosterior.bind(this)]],
      tumorType: [{ disabled: false, value: '' }, [Validators.required]],
      gleason: [{ disabled: false, value: '' }, [Validators.required]]
    });

    if (this.showBirthDate) {
      this.basicDataForm.addControl(
        'birthDate',
        new FormControl(caseData ? { disabled: true, value: this.birthDate ? this.birthDate : caseData.birthDate } : '', [
          CalcValidators.age(18, 100),
          Validators.required
        ])
      );
      this.basicDataForm.controls.birthDate.valueChanges.subscribe((value) => {
        this.birthDate = value;
      });
    }

    this.basicDataForm.controls.diagnosisDate.addValidators(this.fechaNoAnterior.bind(this));

    if (this.formGroupDirective.form) {
      this.formGroupDirective.form.setControl('basicDataForm', this.basicDataForm);
    }
  };

  // Initialize inCourse control listener to enable or disable end date control
  initListenerForm = (): void => {
    this.initHistologyListener();
    this.basicDataForm.controls.diagnosisDate.valueChanges.subscribe((value) => {
      if (!value) {
        this.surgicalForm.get(HAS_TREATMENT).disable();
        this.chemotherapyForm.get(HAS_TREATMENT).disable();
        this.radiotherapyForm.get(HAS_TREATMENT).disable();
        this.immunotherapyForm.get(HAS_TREATMENT).disable();
        this.medicineForm.get(HAS_TREATMENT).disable();
        this.accordion.closeAll();
        this.setValidatorsSurgicalForm(false);
        this.surgicalForm.controls.hasTreatment.setValue(false);
        this.surgicalForm.updateValueAndValidity();
        this.setValidatorsTreatmentForm(this.chemotherapyForm, false);
        this.chemotherapyForm.controls.hasTreatment.setValue(false);
        this.chemotherapyForm.updateValueAndValidity();
        this.setValidatorsTreatmentForm(this.radiotherapyForm, false);
        this.radiotherapyForm.controls.hasTreatment.setValue(false);
        this.radiotherapyForm.updateValueAndValidity();
        this.setValidatorsTreatmentForm(this.immunotherapyForm, false);
        this.immunotherapyForm.controls.hasTreatment.setValue(false);
        this.immunotherapyForm.updateValueAndValidity();
        this.setCorticosteroidsValidator(false);
        this.setOtherMedicineValidator(false);
        this.medicineForm.controls.hasTreatment.setValue(false);
        this.medicineForm.controls.corticosteroids.setValue(false);
        this.medicineForm.controls.other.setValue(false);
        this.medicineForm.updateValueAndValidity();
      } else {
        this.surgicalForm.get(HAS_TREATMENT).enable();
        this.chemotherapyForm.get(HAS_TREATMENT).enable();
        this.radiotherapyForm.get(HAS_TREATMENT).enable();
        this.immunotherapyForm.get(HAS_TREATMENT).enable();
        this.medicineForm.get(HAS_TREATMENT).enable();
      }
    });
    this.basicDataForm.controls[TUMOR_TYPE].valueChanges.subscribe({
      next: (newValue) => {
        if (['NEOPLASIA', 'FIBROMA', 'FIBROMIOMA', 'OTHER'].includes(newValue)) {
          this.histologyFormDisable(true);
          this.histologyDisable = true;
          this.basicDataForm.controls[GLEASON].addValidators([Validators.required]);
          this.basicDataForm.controls[GLEASON].enable({ onlySelf: true });
          if (['FIBROMA', 'FIBROMIOMA'].includes(newValue)) {
            this.basicDataForm.controls[GLEASON].clearValidators();
            this.basicDataForm.controls[GLEASON].reset();
            this.basicDataForm.controls[GLEASON].disable({ onlySelf: true });
          }
        } else if (newValue === 'SARCOMA') {
          this.histologyFormDisable(false);
          this.histologyDisable = false;
          this.basicDataForm.controls[GLEASON].clearValidators();
          this.basicDataForm.controls[GLEASON].reset();
          this.basicDataForm.controls[GLEASON].disable({ onlySelf: true });
          this.histologyForm.controls[PSA].clearValidators();
          this.histologyForm.controls[PSA].reset();
          this.histologyForm.controls[PSA].disable({ onlySelf: true });
        } else {
          this.histologyFormDisable(false);
          this.histologyDisable = false;
          this.basicDataForm.controls[GLEASON].addValidators([Validators.required]);
          this.basicDataForm.controls[GLEASON].enable({ onlySelf: true });
        }
      }
    });

    this.histologyForm.controls[STAGE].valueChanges.subscribe((value) => {
      let tmpVal = value;
      if (typeof value === 'object' && value !== null) {
        tmpVal = value.value;
      }
      this.stageValid = !!tmpVal;
    });

    this.surgicalForm.controls[EXERESIS].valueChanges.subscribe({
      next: (newValue) => this.enableDisableSurgicalControl(this.surgicalForm.controls[INTERVENTION_DATE], newValue)
    });

    this.chemotherapyForm.controls[IN_COURSE].valueChanges.subscribe({
      next: (newValue) => this.enableDisableTherapyControl(this.chemotherapyForm.controls[END_DATE], newValue)
    });

    this.radiotherapyForm.controls[IN_COURSE].valueChanges.subscribe({
      next: (newValue) => this.enableDisableTherapyControl(this.radiotherapyForm.controls[END_DATE], newValue)
    });

    this.immunotherapyForm.controls[IN_COURSE].valueChanges.subscribe({
      next: (newValue) => this.enableDisableTherapyControl(this.immunotherapyForm.controls[END_DATE], newValue)
    });

    this.medicineForm.controls[CORTICOSTEROIDS_IN_COURSE].valueChanges.subscribe({
      next: (newValue) => this.enableDisableMedicineControl(this.medicineForm.controls[CORTICOSTEROIDS_END_DATE], newValue)
    });

    this.medicineForm.controls[OTHER_IN_COURSE].valueChanges.subscribe({
      next: (newValue) => this.enableDisableMedicineControl(this.medicineForm.controls[OTHER_END_DATE], newValue)
    });
  };

  /**
   * Method to initialize a listener in histology and hormonal form fields
   */
  initHistologyListener = (): void => {
    this.histologyForm.controls[HISTOLOGICAL_GRADE].valueChanges
      .pipe(
        tap(() => this.isCalcStage.next(true)),
        debounceTime(500)
      )
      .subscribe(() => (this.histologyForm.controls[HISTOLOGICAL_GRADE].value !== '' ? this.calcStage() : ''));
    this.histologyForm.controls[TUMOR_SIZE].valueChanges
      .pipe(
        tap(() => this.isCalcStage.next(true)),
        debounceTime(500)
      )
      .subscribe(() => (this.histologyForm.controls[TUMOR_SIZE].value !== '' ? this.calcStage() : ''));
    this.histologyForm.controls[LYMPH_NODE_DETAIL].valueChanges
      .pipe(
        tap(() => this.isCalcStage.next(true)),
        debounceTime(500)
      )
      .subscribe(() => (this.histologyForm.controls[LYMPH_NODE_DETAIL].value !== '' ? this.calcStage() : ''));
    this.histologyForm.controls[METASTASE_DETAIL].valueChanges
      .pipe(
        tap(() => this.isCalcStage.next(true)),
        debounceTime(500)
      )
      .subscribe(() => (this.histologyForm.controls[METASTASE_DETAIL].value !== '' ? this.calcStage() : ''));
    this.histologyForm.controls[PSA].valueChanges
      .pipe(
        tap(() => this.isCalcStage.next(true)),
        debounceTime(500)
      )
      .subscribe(() => (this.histologyForm.controls[PSA].value !== '' ? this.calcStage() : ''));
    this.basicDataForm.controls[TUMOR_TYPE].valueChanges
      .pipe(
        tap(() => this.isCalcStage.next(true)),
        debounceTime(500)
      )
      .subscribe(() => (this.basicDataForm.controls[TUMOR_TYPE].value !== '' ? this.calcStage() : ''));
    this.basicDataForm.controls[GLEASON].valueChanges
      .pipe(
        tap(() => this.isCalcStage.next(true)),
        debounceTime(500)
      )
      .subscribe(() => (this.basicDataForm.controls[GLEASON].value !== '' ? this.calcStage() : ''));
  };

  histologyFormDisable(disable: boolean) {
    if (disable) {
      // Limpia todos los validadores del campo 'stage' si tiene alguno
      if (this.histologyForm.controls[STAGE].validator) {
        this.histologyForm.controls[STAGE].clearValidators();
      }
      this.histologyForm.controls[HISTOLOGICAL_GRADE].disable();
      this.histologyForm.controls[STAGE].disable();
      this.histologyForm.controls[LYMPH_NODE_DETAIL].disable();
      this.histologyForm.controls[TUMOR_SIZE].disable();
      this.histologyForm.controls[METASTASE_DETAIL].disable();
      this.histologyForm.controls[PSA].disable();
      this.histologyForm.controls[KI67KEY].disable();
      this.histologyForm.reset({
        histologicalGrade: '',
        stage: '',
        tumorSize: '',
        lymphnodeDetail: '',
        metastaseDetail: '',
        psa: '',
        ki67: ''
      });
    } else {
      this.histologyForm.controls[HISTOLOGICAL_GRADE].enable();
      this.histologyForm.controls[STAGE].enable();
      this.histologyForm.controls[LYMPH_NODE_DETAIL].enable();
      this.histologyForm.controls[TUMOR_SIZE].enable();
      this.histologyForm.controls[METASTASE_DETAIL].enable();
      this.histologyForm.controls[PSA].enable();
      this.histologyForm.controls[KI67KEY].enable();
      this.histologyForm.controls[STAGE].addValidators([Validators.required, this.etapaValidator.bind(this)]);
      this.initHistologyListener();
    }
    this.histologyForm.controls[STAGE].updateValueAndValidity();
  }

  etapaValidator = (control: FormControl) => {
    if (this.errorMsgStage.getValue() !== '') {
      this.errorMsgStage.next('');
      this.cdr.detectChanges();
      return { invalidStage: true };
    }
    if (!control.value) {
      return { required: true };
    }
    return null;
  };

  // Method to enable or disable
  enableDisableSurgicalControl = (control: AbstractControl, value: number | string | boolean): void => {
    if (value === 0 || value === 1) {
      control.addValidators([Validators.required, this.formUtils.dateRangeValidator(this.basicDataForm.controls.diagnosisDate.value)]);
      control.enable({ onlySelf: true });
    } else {
      control.clearValidators();
      control.reset();
      control.disable({ onlySelf: true });
    }
    control.updateValueAndValidity();
  };

  // Method to enable or disable
  enableDisableTherapyControl = (control: AbstractControl, value: number | string | boolean): void => {
    if (value === 1) {
      control.addValidators([Validators.required, this.formUtils.dateRangeValidator(this.basicDataForm.controls.diagnosisDate.value)]);
      control.enable({ onlySelf: true });
    } else {
      control.clearValidators();
      control.reset();
      control.disable({ onlySelf: true });
    }
    control.updateValueAndValidity();
  };

  // Method to enable or disable
  enableDisableMedicineControl = (control: AbstractControl, value: number | string | boolean): void => {
    if (value === 1) {
      control.addValidators([this.formUtils.dateRangeValidator(this.basicDataForm.controls.diagnosisDate.value)]);
      control.enable({ onlySelf: true });
    } else {
      control.clearValidators();
      control.reset();
      control.disable({ onlySelf: true });
    }
    control.updateValueAndValidity();
  };

  isRequired(control: AbstractControl): boolean {
    try {
      return control.hasValidator(Validators.required);
    } catch (error) {
      return false;
    }
  }

  // Method to expand or collapse panel of treatment block except medicine form
  toggleExpansion = (event: MatSlideToggleChange, panel: MatExpansionPanel, formType: string): void => {
    panel.toggle();
    this.updateCollapsableForm(formType, event.checked);
    switch (formType) {
      case 'surgical':
        this.setValidatorsSurgicalForm(event.checked);
        break;
      case 'chemotherapy':
        this.setValidatorsTreatmentForm(this.chemotherapyForm, event.checked);
        break;
      case 'radiotherapy':
        this.setValidatorsTreatmentForm(this.radiotherapyForm, event.checked);
        break;
      case 'immunotherapy':
        this.setValidatorsTreatmentForm(this.immunotherapyForm, event.checked);
        break;
    }
  };

  // Method to expand or collapse panel of medicine form in treatment block
  toggleExpansionMedicineForm = (event: MatCheckboxChange, panel: MatExpansionPanel, option: number): void => {
    panel.toggle();
    if (option === 0) {
      this.setCorticosteroidsValidator(event.checked);
    } else {
      this.setOtherMedicineValidator(event.checked);
    }
  };

  updateCollapsableForm(formName: string, checked: boolean): void {
    // Almaceno las claves de mi formulario
    const keys = Object.keys(this.collapsableForm.controls);
    keys.forEach((key) => {
      // Verificar si el valor del control es igual al parámetro formCheck
      if (key === formName) {
        // Establecer el valor del control en el mismo valor que la clave modificada
        this.collapsableForm.get(key).setValue(checked);
        this.formError = this.checkAllFieldsFalse(this.collapsableForm);
      }
    });
  }

  checkAllFieldsFalse(form: FormGroup): boolean {
    let formCheck = true;
    // Itera sobre todos los controles del formulario
    Object.keys(form.controls).forEach((key) => {
      const control = form.controls[key];
      if (control.value) {
        formCheck = false;
      }
    });

    return formCheck;
  }

  // Method to set validators of Surgical form
  setValidatorsSurgicalForm = (checked: boolean): void => {
    // creamos una variable donde almacenaremos el valor original de typeForm
    if (checked) {
      // Añadimos validacion al campo exersis y al de intervención
      this.surgicalForm.controls[EXERESIS].addValidators(Validators.required);
      this.surgicalForm.controls[INTERVENTION_DATE].addValidators([
        this.formUtils.dateRangeValidator(this.basicDataForm.controls.diagnosisDate.value)
      ]);
      // Suscribimos el cambio de la fecha de diagnostico de forma que siempre valide si es anterior o igual que el campo de fecha de intervención
      // Asignamos a updatedDiagnosedDate el valor actualizado para el validador
      this.subscription = this.basicDataForm.controls.diagnosisDate.valueChanges.subscribe((value) => {
        this.surgicalForm.controls[INTERVENTION_DATE].clearValidators();
        this.surgicalForm.controls[INTERVENTION_DATE].addValidators(this.formUtils.dateRangeValidator(value));
        this.surgicalForm.controls[INTERVENTION_DATE].updateValueAndValidity();
      });
    } else {
      // reiniciamos los validadores y el formulario si no estamos accediendo a el
      this.surgicalForm.controls[EXERESIS].clearValidators();
      this.surgicalForm.controls[INTERVENTION_DATE].clearValidators();
      this.surgicalForm.reset({ hasTreatment: false, exeresis: '', interventionDate: '' });
      this.surgicalForm.updateValueAndValidity();
      this.subscription.unsubscribe();
    }

    // Actualizamos el formulario a consecuencia
    this.surgicalForm.controls[EXERESIS].updateValueAndValidity();
    this.surgicalForm.controls[INTERVENTION_DATE].updateValueAndValidity();
  };

  // Method to set validators of treatment block form except surgical form
  setValidatorsTreatmentForm(form: FormGroup, checked: boolean): void {
    if (checked) {
      form.controls[IN_COURSE].addValidators(Validators.required);
    } else {
      form.controls[IN_COURSE].clearValidators();
      form.reset({
        hasTreatment: false,
        inCourse: '',
        startDate: '',
        endDate: { disabled: true, value: '' }
      });
    }
    form.controls[IN_COURSE].updateValueAndValidity();
  }

  // Method to set Other form validator in Medicine form
  setOtherMedicineValidator = (checked: boolean): void => {
    if (checked) {
      this.medicineForm.controls[OTHER_IN_COURSE].addValidators(Validators.required);
    } else {
      this.medicineForm.controls[OTHER_IN_COURSE].clearValidators();
      this.medicineForm.controls[OTHER_START_DATE].clearValidators();
      this.medicineForm.controls[OTHER_END_DATE].clearValidators();
      this.medicineForm.controls[OTHER_IN_COURSE].reset();
      this.medicineForm.controls[OTHER_START_DATE].reset();
      this.medicineForm.controls[OTHER_END_DATE].reset();
    }
    this.medicineForm.controls[OTHER_IN_COURSE].updateValueAndValidity();
    this.medicineForm.controls[OTHER_START_DATE].updateValueAndValidity();
    this.medicineForm.controls[OTHER_END_DATE].updateValueAndValidity();
  };

  // Method to set Corticosteroids form validator in Medicine form
  setCorticosteroidsValidator = (checked: boolean): void => {
    if (checked) {
      this.medicineForm.controls[CORTICOSTEROIDS_IN_COURSE].addValidators(Validators.required);
    } else {
      this.medicineForm.controls[CORTICOSTEROIDS_IN_COURSE].clearValidators();
      this.medicineForm.controls[CORTICOSTEROIDS_START_DATE].clearValidators();
      this.medicineForm.controls[CORTICOSTEROIDS_END_DATE].clearValidators();
      this.medicineForm.controls[CORTICOSTEROIDS_IN_COURSE].reset();
      this.medicineForm.controls[CORTICOSTEROIDS_START_DATE].reset();
      this.medicineForm.controls[CORTICOSTEROIDS_END_DATE].reset();
    }
    this.medicineForm.controls[CORTICOSTEROIDS_IN_COURSE].updateValueAndValidity();
    this.medicineForm.controls[CORTICOSTEROIDS_START_DATE].updateValueAndValidity();
    this.medicineForm.controls[CORTICOSTEROIDS_END_DATE].updateValueAndValidity();
  };

  // Method to check if we have a case in progress assigned
  hasCaseInCourse = (): boolean => this.caseInProgressSrv.hasInCourse();

  hasCaseInParams = (): boolean => {
    const idCaseValue = this.activatedRouter.snapshot.paramMap.get('idCase');
    // Devolvemos true si no es null o valor vacío
    return idCaseValue !== null && idCaseValue !== '';
  };

  // Method which set age form from date change detected
  calcAge = (data: MatDatepickerInputEvent<Date>): void => {
    const date = moment(data.value);
    this.basicDataForm.get('age').setValue(moment().year() - date.year());
  };

  fechaNoPosterior = (control: FormControl) => {
    const enteredDate = new Date(control.value);
    const actualDate = new Date();
    actualDate.setDate(actualDate.getDate() - 1);
    actualDate.setHours(0, 0, 0, 0); // Para comparar solo las fechas sin tener en cuenta la hora
    return enteredDate > actualDate ? { fechaPosterior: true } : null;
  };

  fechaNoAnterior = (control: FormControl) => {
    const enteredDate = new Date(control.value);
    const actualDate = new Date(this.basicDataForm.controls.birthDate.value);
    actualDate.setHours(0, 0, 0, 0); // Para comparar solo las fechas sin tener en cuenta la hora
    return enteredDate < actualDate ? { fechaAnterior: true } : null;
  };

  // Method to clean every form of this page
  cleanForms = (): void => {
    // Call ngOnit who initialize every form. So it has been resetted
    this.accordion.closeAll();
    this.basicDataForm.clearAsyncValidators();

    if (this.hasCaseInCourse() || this.hasCaseInParams()) {
      this.stored = JSON.parse(localStorage?.getItem('CASE_IN_COURSE'));
      this.basicDataForm.reset({
        age: this.stored?.age
      });
      this.basicDataForm.controls['birthDate'].setValue(new Date(new Date().getFullYear() - this.stored?.age, 0, 1));
    } else {
      this.basicDataForm.reset();
    }
    this.basicDataForm.updateValueAndValidity();
    this.setValidatorsSurgicalForm(false);
    this.surgicalForm.controls.hasTreatment.setValue(false);
    this.surgicalForm.updateValueAndValidity();
    this.setValidatorsTreatmentForm(this.chemotherapyForm, false);
    this.chemotherapyForm.controls.hasTreatment.setValue(false);
    this.chemotherapyForm.updateValueAndValidity();
    this.setValidatorsTreatmentForm(this.radiotherapyForm, false);
    this.radiotherapyForm.controls.hasTreatment.setValue(false);
    this.radiotherapyForm.updateValueAndValidity();
    this.setValidatorsTreatmentForm(this.immunotherapyForm, false);
    this.immunotherapyForm.controls.hasTreatment.setValue(false);
    this.immunotherapyForm.updateValueAndValidity();
    this.setCorticosteroidsValidator(false);
    this.setOtherMedicineValidator(false);
    this.medicineForm.controls.hasTreatment.setValue(false);
    this.medicineForm.controls.corticosteroids.setValue(false);
    this.medicineForm.controls.other.setValue(false);
    this.medicineForm.updateValueAndValidity();
    if (this.treatmentForm.controls.treatmentList) {
      while ((this.treatmentForm.controls.treatmentList as FormArray).length) {
        (this.treatmentForm.controls.treatmentList as FormArray).removeAt(0);
      }
    }
    this.histologyForm.clearAsyncValidators();
    this.histologyForm.reset();
    this.histologyForm.updateValueAndValidity();
    this.collapsableForm.reset({
      surgical: false,
      chemotherapy: false,
      radiotherapy: false
    });
    this.formError = this.checkAllFieldsFalse(this.collapsableForm);
    this.errorMsgStage.next('');
    this.cdr.detectChanges();
  };

  shouldBeDisabled = (): boolean => {
    let s = false;
    if (!this.histologyDisable) {
      s = !this.stageValid;
    }
    const c = this.isNotValidateToCalc();
    return s || c;
  };

  isNotValidateToCalc = (): boolean => {
    if (
      this.formError ||
      !this.basicDataForm.valid ||
      !this.surgicalForm.valid ||
      !this.chemotherapyForm.valid ||
      !this.radiotherapyForm.valid ||
      !this.immunotherapyForm.valid ||
      !this.medicineForm.valid ||
      !this.treatmentForm.valid ||
      (!this.histologyDisable && !this.histologyForm.valid)
    ) {
      return true;
    }
    return false;
  };

  /**
   * Method to get the from form data
   */
  calcStage = (): void => {
    const { histologicalGrade, tumorSize, lymphnodeDetail, metastaseDetail, psa } = this.histologyForm.getRawValue();
    const { tumorType, gleason } = this.basicDataForm.getRawValue();

    const calcular =
      tumorType && lymphnodeDetail && lymphnodeDetail.length && metastaseDetail && metastaseDetail.length && tumorSize && tumorSize.length;

    if (calcular && this.doCalcStage) {
      this.doCalcStage = false;
      const loader = this.loaderSrv.showSpinner();
      const data = {
        histologicalGrade: histologicalGrade?.value ?? histologicalGrade,
        lymphnodeDetail: lymphnodeDetail?.value ?? lymphnodeDetail,
        metastaseDetail: metastaseDetail?.value ?? metastaseDetail,
        tumorSize: tumorSize?.value ?? tumorSize,
        psa: psa?.value ?? psa,
        tumorType: tumorType?.value ?? tumorType,
        gleason: gleason?.value ?? gleason
      };

      this.prostateCancerService.prostateCancerStageCalcPost(data).subscribe(
        (response) => {
          this.histologyForm.controls[STAGE].setValue(response.stage);
          this.histologyForm.controls[STAGE].markAsTouched();
          this.histologyForm.controls[STAGE].disable({ onlySelf: true });
          this.histologyForm.updateValueAndValidity();
          this.errorMsgStage.next(response.msg);
          this.isCalcStage.next(false);
          this.cdr.detectChanges();
          this.doCalcStage = true;
          loader.close();
        },
        () => {
          this.doCalcStage = true;
          loader.close();
        }
      );
    } else if (this.doCalcStage) {
      this.isCalcStage.next(false);
      this.cdr.detectChanges();
    }
  };

  // Method which call calc service with the form data
  calc = (): void => {
    this.markAllAsTouched();
    if (!this.isNotValidateToCalc()) {
      const loader = this.loaderSrv.showSpinner();
      const data = this.prepareCalcData();
      this.prostateCancerService.prostateCancerPost(data).subscribe({
        next: (response) => {
          const res = (response as any).err?.msg === 'The stage is incompatible';
          loader.close();

          this.dataResult.emit({ data, result: response });
        }
      });
    }
  };

  markAllAsTouched = (): void => {
    this.surgicalForm.markAllAsTouched();
    this.chemotherapyForm.markAllAsTouched();
    this.radiotherapyForm.markAllAsTouched();
    this.immunotherapyForm.markAllAsTouched();
    this.medicineForm.markAllAsTouched();
    this.treatmentForm.markAllAsTouched();
    this.histologyForm.markAllAsTouched();
    this.basicDataForm.markAllAsTouched();
  };

  // Method to prepare data for calc service
  prepareCalcData = (): any => {
    const { histologicalGrade, stage, lymphnodeDetail, metastaseDetail, tumorSize, ki67, psa } = this.histologyForm.getRawValue();

    const value: IProstateCancerForm = {
      basicData: {
        birthDate: this.basicDataForm.get('birthDate').value,
        age: this.basicDataForm.get('age').value,
        rightToForget: this.basicDataForm.get(RIGHT_TO_FORGET).value,
        diagnosisDate: this.basicDataForm.get(DIAGNOSIS_DATE).value,
        tumorType: this.basicDataForm.get(TUMOR_TYPE).value,
        gleason: this.basicDataForm.get(GLEASON).value
      },
      treatmentType: {
        surgical:
          this.surgicalForm.get(HAS_TREATMENT).value === false
            ? null
            : {
                exeresis: this.surgicalForm.get(EXERESIS).value,
                interventionDate: this.surgicalForm.get(INTERVENTION_DATE).value
              },
        chemotherapy:
          this.chemotherapyForm.get(HAS_TREATMENT).value === false
            ? null
            : {
                inCourse: this.chemotherapyForm.get(IN_COURSE).value,
                startDate: this.chemotherapyForm.get(START_DATE).value,
                endDate: this.chemotherapyForm.get(END_DATE).value ?? null
              },
        radiotherapy:
          this.radiotherapyForm.get(HAS_TREATMENT).value === false
            ? null
            : {
                inCourse: this.radiotherapyForm.get(IN_COURSE).value,
                startDate: this.radiotherapyForm.get(START_DATE).value,
                endDate: this.radiotherapyForm.get(END_DATE).value ?? null
              },
        immunoTherapy:
          this.immunotherapyForm.get(HAS_TREATMENT).value === false
            ? null
            : {
                inCourse: this.immunotherapyForm.get(IN_COURSE).value,
                startDate: this.immunotherapyForm.get(START_DATE).value,
                endDate: this.immunotherapyForm.get(END_DATE).value ?? null
              },
        medicines:
          this.medicineForm.get(HAS_TREATMENT).value === false
            ? null
            : {
                corticosteroids: this.medicineForm.get('corticosteroids').value,
                corticosteroidsInCourse: this.medicineForm.get(CORTICOSTEROIDS_IN_COURSE).value,
                corticosteroidsStartDate: this.medicineForm.get(CORTICOSTEROIDS_START_DATE).value,
                corticosteroidsEndDate: this.medicineForm.get(CORTICOSTEROIDS_END_DATE).value
                  ? this.medicineForm.get(CORTICOSTEROIDS_END_DATE).value
                  : null,
                other: this.medicineForm.get('other').value,
                otherInCourse: this.medicineForm.get(OTHER_IN_COURSE).value,
                otherStartDate: this.medicineForm.get(OTHER_START_DATE).value,
                otherEndDate: this.medicineForm.get(OTHER_END_DATE).value ? this.medicineForm.get(OTHER_END_DATE).value : null
              }
      },
      treatment: (this.treatmentForm.controls.treatmentList as FormArray).getRawValue(),
      histology: {
        histologicalGrade: histologicalGrade?.value ?? histologicalGrade,
        stage: stage?.value ?? stage,
        lymphnodeDetail: lymphnodeDetail?.value ?? lymphnodeDetail,
        metastaseDetail: metastaseDetail?.value ?? metastaseDetail,
        tumorSize: tumorSize?.value ?? tumorSize,
        ki67: ki67?.value ?? ki67,
        psa: psa?.value ?? psa
      }
    };
    return value;
  };

  // Method to patch value to forms in edit mode
  patchDataValues = (): void => {
    this.resetHistology();

    // Histology form
    this.histologyForm.patchValue(this.data.histology);
    this.histologyForm.updateValueAndValidity();
    // Medical treatment form @Input in component app-medical-detail
    // Treatment type
    if (this.data.basicData) {
      this.basicDataForm.patchValue(this.data.basicData);
      // this.setValidatorsSurgicalForm(true);
    }

    if (this.data.treatmentType.surgical) {
      this.surgicalForm.get(HAS_TREATMENT).setValue(true);
      this.surgicalForm.patchValue(this.data.treatmentType.surgical);
      this.setValidatorsSurgicalForm(true);
    }
    if (this.data.treatmentType.chemotherapy) {
      this.chemotherapyForm.get(HAS_TREATMENT).setValue(true);
      this.chemotherapyForm.patchValue(this.data.treatmentType.chemotherapy);
      this.setValidatorsTreatmentForm(this.chemotherapyForm, true);
    }
    if (this.data.treatmentType.radiotherapy) {
      this.radiotherapyForm.get(HAS_TREATMENT).setValue(true);
      this.radiotherapyForm.patchValue(this.data.treatmentType.radiotherapy);
      this.setValidatorsTreatmentForm(this.radiotherapyForm, true);
    }
    if (this.data.treatmentType.immunoTherapy) {
      this.immunotherapyForm.get(HAS_TREATMENT).setValue(true);
      this.immunotherapyForm.patchValue(this.data.treatmentType.immunoTherapy);
      this.setValidatorsTreatmentForm(this.immunotherapyForm, true);
    }
    if (this.data.treatmentType.medicines) {
      this.medicineForm.get(HAS_TREATMENT).setValue(true);
      this.medicineForm.patchValue(this.data.treatmentType.medicines);
      if (this.data.treatmentType.medicines.corticosteroids) {
        this.setCorticosteroidsValidator(true);
      }
      if (this.data.treatmentType.medicines.other) {
        this.setOtherMedicineValidator(true);
      }
    }
  };

  resetHistology = () => {
    [
      this.histologyForm.controls[PSA],
      this.histologyForm.controls[KI67KEY],
      this.histologyForm.controls[METASTASE_DETAIL],
      this.histologyForm.controls[STAGE]
    ].forEach((ctrl: AbstractControl) => {
      ctrl.clearValidators();
      ctrl.reset();
      ctrl.enable({ onlySelf: true });
      ctrl.updateValueAndValidity();
    });
    this.histologyForm.controls[STAGE].addValidators(Validators.required);
  };
}
